
import {
  normalizeEmail as normalizeSharedEmail,
  normalizeIban as normalizeSharedIban,
  normalizePhoneNumber as normalizeSharedPhoneNumber,
} from '@aeppic/shared/text-normalization'

export class FieldFormatter {
  lowercase(value: string) {
    return value.toLowerCase()
  }
  uppercase(value: string) {
    return value.toUpperCase()
  }
  capitalize(value: string) {
    return value.charAt(0).toUpperCase() + value.slice(1)
  }
  trim(value: string) {
    return value.trim()
  }
  length(value: string, maxLength: number) {
    return value.slice(0, maxLength)
  }
  removeChars(value: string, ...excludedCharacters: CharacterMatcherArgument[]) {
    const excludedCharactersRegex = buildCharacterMatchers('removeChars', excludedCharacters)
    
    return value.split('').filter(char => !excludedCharactersRegex.some(excludedChar => {
      return excludedChar === char
    })).join('')
  }
  excludeChars(value: string, ...excludedCharacters: CharacterMatcherArgument[]) {
    const excludedCharactersRegex = buildCharacterMatchers('excludeChars', excludedCharacters)
    
    return value.split('').filter(char => !excludedCharactersRegex.some(excludedChar => {
      return excludedChar === char
    })).join('')
  }
  keepChars(value: string, ...allowedCharacters: CharacterMatcherArgument[]) {
    const allowedCharactersRegex = buildCharacterMatchers('keepChars', allowedCharacters)

    return value.split('').filter(char => allowedCharactersRegex.some(allowedChar => {
      return allowedChar === char
    })).join('')
  }
  allowChars(value: string, ...allowedCharacters: CharacterMatcherArgument[]) {
    const allowedCharactersRegex = buildCharacterMatchers('allowChars', allowedCharacters)

    return value.split('').filter(char => allowedCharactersRegex.some(allowedChar => {
      return allowedChar === char
    })).join('')
  }
  removeMatching(value: string, ...matchers: TextMatcherArgument[]) {
    const ranges = findMatcherRanges(value, normalizeFormatterArguments('removeMatching', matchers))
    return applyRanges(value, ranges, { keepMatches: false })
  }
  excludeMatching(value: string, ...matchers: TextMatcherArgument[]) {
    const ranges = findMatcherRanges(value, normalizeFormatterArguments('excludeMatching', matchers))
    return applyRanges(value, ranges, { keepMatches: false })
  }
  keepMatching(value: string, ...matchers: TextMatcherArgument[]) {
    const normalizedMatchers = normalizeFormatterArguments('keepMatching', matchers)
    const ranges = findMatcherRanges(value, normalizedMatchers)

    if (ranges.length === 0 && isPotentialNumericPhonePrefix(value, normalizedMatchers)) {
      return value
    }

    return applyRanges(value, ranges, { keepMatches: true })
  }
  allowMatching(value: string, ...matchers: TextMatcherArgument[]) {
    const ranges = findMatcherRanges(value, normalizeFormatterArguments('allowMatching', matchers))
    return applyRanges(value, ranges, { keepMatches: true })
  }
  normalizePhoneNumber(value: string) {
    return normalizeSharedPhoneNumber(value)
  }
  normalizePhone(value: string) {
    return this.normalizePhoneNumber(value)
  }
  digits(value: string) {
    return this.keepChars(value, '0123456789')
  }
  alphanum(value: string) {
    return value.normalize('NFC').replace(/[^\p{L}\p{N}]+/gu, '')
  }
  asciiAlphanum(value: string) {
    return this.keepChars(transliterateToAscii(value), 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789')
  }
  slug(value: string) {
    return transliterateToAscii(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }
  jsIdentifier(value: string) {
    const words = transliterateToAscii(value)
      .trim()
      .split(/[^a-zA-Z0-9_$]+/)
      .filter(Boolean)

    let identifier = words.map((word, index) => {
      if (index === 0) {
        return word.charAt(0).toLowerCase() + word.slice(1)
      }

      return word.charAt(0).toUpperCase() + word.slice(1)
    }).join('')

    identifier = identifier.replace(/[^a-zA-Z0-9_$]/g, '')

    if (!identifier) {
      return '_'
    }

    if (!/^[a-zA-Z_$]/.test(identifier)) {
      return `_${identifier}`
    }

    return identifier
  }
  normalizeEmail(value: string) {
    return normalizeSharedEmail(value)
  }
  normalizeIban(value: string) {
    return normalizeSharedIban(value)
  }
  normalizeUuidV4(value: string) {
    const normalized = value.trim().toLowerCase()

    if (!UUID_V4_REGEX.test(normalized)) {
      return ''
    }

    return normalized
  }
  safeFileName(value: string) {
    return safeFileName(value)
  }
  replace(value: string, searchValue: string|RegExp, replaceValue: string) {
    return value.replace(searchValue, replaceValue)
  }
  prefix(value, prefix) {
    if (value.startsWith(prefix)) {
      return value
    }
    return prefix + value
  }
  suffix(value, suffix) {
    if (value.endsWith(suffix)) {
      return value
    }
    return value + suffix
  }
  pattern(value, pattern) {
    return formatStringByPattern(value, pattern)
  }
}

export type TextFormatterContext = {
  functions?: any
  Math?: Math
  DateTime?: any
  DateTimeDuration?: any
  builtInFormats?: FieldFormatter
}

export type TextFormatter = (value: any, context?: TextFormatterContext) => string

type CharacterMatcherArgument = string|string[]
type TextMatcher = string|RegExp
type TextMatcherArgument = TextMatcher|TextMatcher[]

const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/
const WINDOWS_RESERVED_FILE_NAMES = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\..*)?$/i
const DEFAULT_FILE_NAME = 'untitled'
const MAX_FILE_NAME_LENGTH = 255
const DEFAULT_BUILT_IN_FORMATTER = new FieldFormatter()
const GERMAN_TRANSLITERATION: { [key: string]: string } = {
  '\u00c4': 'Ae',
  '\u00d6': 'Oe',
  '\u00dc': 'Ue',
  '\u00e4': 'ae',
  '\u00f6': 'oe',
  '\u00fc': 'ue',
  '\u00df': 'ss',
}

export class Formatting {
  private _formatters = new Map<string, TextFormatter>()

  formatText(formattingExpression: string): TextFormatter
  formatText(value: any, formattingExpression: string, context?: TextFormatterContext): string
  formatText(valueOrFormattingExpression: any, formattingExpression?: string, context?: TextFormatterContext): string|TextFormatter {
    if (formattingExpression == null) {
      return this.compileText(valueOrFormattingExpression)
    }

    return this.compileText(formattingExpression)(valueOrFormattingExpression, context)
  }

  compileText(formattingExpression: string): TextFormatter {
    const cachedFormatter = this._formatters.get(formattingExpression)

    if (cachedFormatter) {
      return cachedFormatter
    }

    const formatter = compileTextFormatter(formattingExpression)
    this._formatters.set(formattingExpression, formatter)
    return formatter
  }
}

export function compileTextFormatter(formattingExpression: string): TextFormatter {
  const steps = createFormattingStepCodes(formattingExpression)
  const code = `context = context || {}\n`
             + `const functions = context.functions || {}\n`
             + `const Math = context.Math || defaultMath\n`
             + `const DateTime = context.DateTime\n`
             + `const DateTimeDuration = context.DateTimeDuration\n`
             + `current = current == null ? '' : String(current)\n`
             + `${steps.join('\n')}\n`
             + `return current\n`

  const compiledFormatter = new Function('current', 'context', 'builtInFormats', 'defaultMath', code)

  return (value: any, context: TextFormatterContext = {}) => {
    return compiledFormatter(value, context, context.builtInFormats ?? DEFAULT_BUILT_IN_FORMATTER, Math)
  }
}

export type FormattingStepCodeOptions = {
  onUnknownFunction?: (functionName: string) => void
}

export function createFormattingStepCodes(formattingExpression: string, options: FormattingStepCodeOptions = {}): string[] {
  return splitFormattingSteps(formattingExpression)
    .map(exp => createFormattingStepCode(exp, options))
    .filter(Boolean)
}

export function createFormattingStepCode(expression: string, options: FormattingStepCodeOptions = {}): string {
  const exp = expression.trim()

  if (!exp) {
    return ''
  }

  if (exp.startsWith('functions.')) {
    return createSafeFormattingStatementCode(`current = ${exp}`)
  }

  const paramStart = exp.indexOf('(')

  if (paramStart === -1) {
    const functionName = exp

    if (isKnownFieldFormatterFunction(functionName)) {
      return createSafeFormattingStatementCode(`current = builtInFormats.${functionName}(current)`)
    }

    options.onUnknownFunction?.(functionName)
    return ''
  }

  if (!exp.endsWith(')')) {
    options.onUnknownFunction?.(exp.substring(0, paramStart).trim())
    return ''
  }

  const functionName = exp.substring(0, paramStart).trim()

  if (!isKnownFieldFormatterFunction(functionName)) {
    options.onUnknownFunction?.(functionName)
    return ''
  }

  const parameters = exp.substring(paramStart + 1, exp.length - 1)
  return createSafeFormattingStatementCode(`current = builtInFormats.${functionName}(current, ${parameters})`)
}

function createSafeFormattingStatementCode(statement: string) {
  return `try { ${statement} } catch (e) {}`
}

function transliterateToAscii(value: string) {
  return value
    .replace(/[\u00c4\u00d6\u00dc\u00e4\u00f6\u00fc\u00df]/g, char => GERMAN_TRANSLITERATION[char] || char)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
}

function safeFileName(value: string) {
  let fileName = transliterateToAscii(value)
    .replace(/[<>:"/\\|?*\x00-\x1F]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/-\./g, '.')
    .replace(/\.-/g, '.')
    .replace(/^[. -]+|[. -]+$/g, '')

  if (!fileName || fileName === '.' || fileName === '..') {
    fileName = DEFAULT_FILE_NAME
  }

  if (WINDOWS_RESERVED_FILE_NAMES.test(fileName)) {
    fileName = `_${fileName}`
  }

  if (fileName.length <= MAX_FILE_NAME_LENGTH) {
    return fileName
  }

  const extensionIndex = fileName.lastIndexOf('.')
  const hasExtension = extensionIndex > 0 && extensionIndex < fileName.length - 1

  if (!hasExtension) {
    return fileName.slice(0, MAX_FILE_NAME_LENGTH)
  }

  const extension = fileName.slice(extensionIndex)
  const baseName = fileName.slice(0, MAX_FILE_NAME_LENGTH - extension.length)
  return `${baseName}${extension}`
}

function normalizeFormatterArguments<T>(formatterName: string, args: (T|T[])[]): T[] {
  if (args.length === 0) {
    return []
  }

  const [first, ...rest] = args

  if (Array.isArray(first)) {
    if (rest.length > 0) {
      throw new Error(`${formatterName} accepts either one array argument or multiple arguments, not both`)
    }

    return first
  }

  return args as T[]
}

function buildCharacterMatchers(formatterName: string, characterGroups: CharacterMatcherArgument[]): string[] {
  const matchers: string[] = []

  for (const characters of normalizeFormatterArguments(formatterName, characterGroups)) {
    if (typeof characters !== 'string') {
      throw new Error(`${formatterName} only accepts strings or an array of strings`)
    }

    if (characters.length === 0) {
      throw new Error(`${formatterName} does not accept empty character groups`)
    }

    matchers.push(...characters.split(''))
  }

  return matchers
}

function findMatcherRanges(value: string, matchers: TextMatcher[]) {
  const ranges: { start: number, end: number }[] = []

  for (const matcher of matchers) {
    if (typeof matcher === 'string') {
      if (matcher.length === 0) {
        throw new Error('Text matchers must not be empty')
      }

      let start = value.indexOf(matcher)

      while (start !== -1) {
        ranges.push({ start, end: start + matcher.length })
        start = value.indexOf(matcher, start + matcher.length)
      }

      continue
    }

    if (!(matcher instanceof RegExp)) {
      throw new Error('Text matchers must be strings or regular expressions')
    }

    const flags = matcher.flags.replace('y', '')
    const globalFlags = flags.includes('g') ? flags : `${flags}g`
    const globalMatcher = new RegExp(matcher.source, globalFlags)
    let match: RegExpExecArray|null

    while ((match = globalMatcher.exec(value)) !== null) {
      if (match[0].length === 0) {
        globalMatcher.lastIndex += 1
        continue
      }

      ranges.push({ start: match.index, end: match.index + match[0].length })
    }
  }

  return mergeRanges(ranges)
}

function isPotentialNumericPhonePrefix(value: string, matchers: TextMatcher[]) {
  if (matchers.length !== 1 || !(matchers[0] instanceof RegExp)) {
    return false
  }

  const source = matchers[0].source
  const match = /^\^\\\+\?(?:\[0-9\]|\\d)\{(\d+),(\d+)\}\$$/.exec(source)

  if (!match) {
    return false
  }

  if (!/^\+?\d*$/.test(value)) {
    return false
  }

  const digits = value.replace(/\D/g, '')
  const minLength = Number(match[1])
  const maxLength = Number(match[2])

  return digits.length > 0 && digits.length < minLength && digits.length <= maxLength
}

function mergeRanges(ranges: { start: number, end: number }[]) {
  const sortedRanges = ranges.sort((a, b) => a.start - b.start || a.end - b.end)
  const mergedRanges: { start: number, end: number }[] = []

  for (const range of sortedRanges) {
    const previous = mergedRanges[mergedRanges.length - 1]

    if (previous && range.start <= previous.end) {
      previous.end = Math.max(previous.end, range.end)
    } else {
      mergedRanges.push({ ...range })
    }
  }

  return mergedRanges
}

function applyRanges(value: string, ranges: { start: number, end: number }[], { keepMatches }: { keepMatches: boolean }) {
  if (keepMatches) {
    return ranges.map(range => value.slice(range.start, range.end)).join('')
  }

  let result = ''
  let lastIndex = 0

  for (const range of ranges) {
    result += value.slice(lastIndex, range.start)
    lastIndex = range.end
  }

  result += value.slice(lastIndex)
  return result
}

let knownFunctions: string[] = null

export function getKnownFieldFormatterFunctions() {
  if (!knownFunctions) {
    knownFunctions = Object.getOwnPropertyNames(FieldFormatter.prototype).filter(name => name !== 'constructor')
  }

  return knownFunctions
}

export function isKnownFieldFormatterFunction(name: string) {
  return getKnownFieldFormatterFunctions().includes(name)
}

export function splitFormattingSteps(formattingExpression: string): string[] {
  const steps: string[] = []
  let stepStart = 0
  let depth = 0
  let quote: string|null = null
  let escaped = false
  let inRegex = false

  for (let i = 0; i < formattingExpression.length; i += 1) {
    const char = formattingExpression[i]

    if (quote) {
      if (escaped) {
        escaped = false
      } else if (char === '\\') {
        escaped = true
      } else if (char === quote) {
        quote = null
      }
      continue
    }

    if (inRegex) {
      if (escaped) {
        escaped = false
      } else if (char === '\\') {
        escaped = true
      } else if (char === '/') {
        inRegex = false
      }
      continue
    }

    if (char === '"' || char === '\'' || char === '`') {
      quote = char
      continue
    }

    if (char === '/' && looksLikeRegexStart(formattingExpression, i)) {
      inRegex = true
      continue
    }

    if (char === '(' || char === '[' || char === '{') {
      depth += 1
      continue
    }

    if (char === ')' || char === ']' || char === '}') {
      depth = Math.max(0, depth - 1)
      continue
    }

    if (char === ',' && depth === 0) {
      steps.push(formattingExpression.substring(stepStart, i).trim())
      stepStart = i + 1
    }
  }

  steps.push(formattingExpression.substring(stepStart).trim())
  return steps.filter(Boolean)
}

function looksLikeRegexStart(expression: string, index: number) {
  const previous = expression.slice(0, index).trimEnd()

  if (!previous) {
    return true
  }

  return /[(,=:[!&|?{};]/.test(previous[previous.length - 1])
}

const PATTERN_TOKENS = {
  '0': {pattern: /\d/, _default: '0'},
  '9': {pattern: /\d/, optional: true},
  // '#': {pattern: /\d/, optional: true, recursive: true},
  'A': {pattern: /[a-zA-Z0-9]/},
  'S': {pattern: /[a-zA-Z]/},
  'U': {pattern: /[a-zA-Z]/, transform: function(c) { return c.toLocaleUpperCase(); }},
  'L': {pattern: /[a-zA-Z]/, transform: function(c) { return c.toLocaleLowerCase(); }},
  // '$': {escape: true}
}

function formatStringByPattern(value: string, pattern: string) { // , options: { reverse: boolean } = { reverse: false }) {
  let patternArray = pattern.split('')
  let valueArray = value.split('')

  let formattedValue = ''
  let valueIndex = 0

  for (const pattern of patternArray) {
    if (valueIndex >= valueArray.length) {
      break
    }

    let token = PATTERN_TOKENS[pattern]

    if (token) {
      let valueChar = valueArray[valueIndex]

      if (token.pattern.test(valueChar)) {
        formattedValue += token.transform ? token.transform(valueChar) : valueChar
        valueIndex++
      } else if (token.optional) {
        valueIndex++
      } else {
        formattedValue += token._default ?? ''
      }
    } else {
      formattedValue += pattern

      if (valueArray[valueIndex] === pattern) {
        valueIndex++
      }
    }
  }

  return formattedValue
}
